/**
 * The "Vault" colour data, as plain values.
 *
 * Split out of `paper-theme.ts` so the WCAG guard in `contrast.test.ts` can
 * read the palette without loading react-native-paper under jsdom — the same
 * reason `resolveScheme` lives in `lib/` rather than in the theme provider.
 * (Paper's barrel reaches react-native-safe-area-context, which resolves to
 * its Flow-typed native entry outside the Vite browser pipeline.)
 *
 * These are the *overrides* Sparkie applies; `paper-theme.ts` spreads them
 * over `MD3LightTheme.colors` / `MD3DarkTheme.colors` for the roles it does
 * not set. `satisfies` keeps every key checked against Paper's own colour type
 * while leaving the values concretely typed for the audit.
 *
 * Every pair here is audited for WCAG 2.2 Level AA and locked by
 * `contrast.test.ts`; the audit, and the two values it moved off the mobile
 * palette, are recorded at the top of `paper-theme.ts`. `mobile/theme/` stays
 * canonical for everything else and is synced by hand (research D9) — when a
 * colour moves there, mirror it here and run the suite.
 */

import type { MD3Theme } from "react-native-paper";

/** Colours the app needs that MD3 has no role for. */
export interface StatusColors {
  /** Transcription or AI generation finished successfully. */
  success: string;
}

type ColorOverrides = Partial<MD3Theme["colors"]>;

export const vaultLightColors = {
  primary: "#2A4747",
  onPrimary: "#FFFFFF",
  primaryContainer: "#D0D7D7",
  onPrimaryContainer: "#172727",

  secondary: "#3B3B58",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#DCDCE1",
  onSecondaryContainer: "#1E1E2C",

  // Golden Earth, darkened 3.5 points of HSL lightness for the web (hue and
  // saturation untouched): the seed reads at only 4.46:1 on a raised surface.
  // See the audit at the top of `paper-theme.ts`.
  tertiary: "#8A581B",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#F4D35E",
  onTertiaryContainer: "#3D270C",

  // Pulled cooler than the ochre so "failed" never reads as "recording".
  error: "#A2263C",
  onError: "#FFFFFF",
  errorContainer: "#EED8DC",
  onErrorContainer: "#49111B",

  background: "#F9F9F9",
  // Neutral rather than teal-tinted, so `primary` still reads as an accent
  // against body text.
  onBackground: "#1B1D1F",
  surface: "#F9F9F9",
  onSurface: "#1B1D1F",
  surfaceVariant: "#D8DDE2",
  onSurfaceVariant: "#494E54",

  outline: "#6E767F",
  outlineVariant: "#CCD2DA",

  // Surface tinted with `primary` at MD3's 5/8/11/12/14% overlays. Paper's
  // stock ramp is tinted with MD3's default primary, so leaving it in place
  // would give every raised surface — cards, menus — a violet cast that does
  // not belong to this palette.
  elevation: {
    level0: "transparent",
    level1: "#EFF0F0",
    level2: "#E8EBEB",
    level3: "#E2E5E5",
    level4: "#E0E4E4",
    level5: "#DCE0E0",
  },

  inverseSurface: "#223939",
  inverseOnSurface: "#F2F4F4",
  inversePrimary: "#98CDCD",

  surfaceDisabled: "rgba(27, 29, 31, 0.12)",
  onSurfaceDisabled: "rgba(27, 29, 31, 0.38)",
  backdrop: "rgba(34, 57, 57, 0.4)",
  shadow: "#000000",
  scrim: "#000000",
} satisfies ColorOverrides;

export const vaultLightStatus: StatusColors = {
  // Darkened 1.4 points of HSL lightness for the web, same reason as
  // `tertiary` above.
  success: "#356F3C",
};

export const vaultDarkColors = {
  // Lifted in HSL rather than mixed toward white: the seed is only 26%
  // saturated, so a white mix desaturates it to near-grey and the teal
  // identity is lost entirely.
  primary: "#98CDCD",
  onPrimary: "#0B3234",
  primaryContainer: "#1E4A4C",
  onPrimaryContainer: "#B9E3E3",

  secondary: "#A2AEBB",
  onSecondary: "#3B3B58",
  secondaryContainer: "#3B3B58",
  onSecondaryContainer: "#D5DBE0",

  tertiary: "#F4D35E",
  onTertiary: "#452C0E",
  tertiaryContainer: "#99621E",
  // Near-white: pure white on Golden Earth tops out at 5.10:1, so the
  // on-colour has to sit close to it to clear AA.
  onTertiaryContainer: "#FDF8E7",

  error: "#F2B8AE",
  onError: "#5C1710",
  errorContainer: "#7D2A22",
  onErrorContainer: "#FFDAD4",

  background: "#181823",
  onBackground: "#E4E6E9",
  surface: "#181823",
  onSurface: "#E4E6E9",
  surfaceVariant: "#363944",
  onSurfaceVariant: "#A2AEBB",

  outline: "#7E8892",
  outlineVariant: "#3F424E",

  elevation: {
    level0: "transparent",
    level1: "#20202B",
    level2: "#24252F",
    level3: "#292A34",
    level4: "#2A2B35",
    level5: "#2D2F38",
  },

  inverseSurface: "#E4E6E9",
  inverseOnSurface: "#2F2F39",
  inversePrimary: "#2A4747",

  surfaceDisabled: "rgba(228, 230, 233, 0.12)",
  onSurfaceDisabled: "rgba(228, 230, 233, 0.38)",
  backdrop: "rgba(34, 57, 57, 0.4)",
  shadow: "#000000",
  scrim: "#000000",
} satisfies ColorOverrides;

export const vaultDarkStatus: StatusColors = {
  success: "#8FD69A",
};
